import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

// Para cambiar el video: reemplaza el ID de YouTube abajo
const COMMUNITY_VIDEO_ID = 'vwoE5cEJbxY';
const COMMUNITY_VIDEO_THUMBNAIL = 'https://static.readdy.ai/image/38235d25ca124ed59b2cbc0dcd639844/633b004704a6b64e716e576685a5e3ff.png';

function CommunityIntro() {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
        <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6 tracking-tight">Our Community Homes Program</h2>
        <p className="text-gray-600 text-lg leading-relaxed mb-4">
          At ALFA Decorators, we believe in giving back to the community that supports us. Through our Community Homes Program, we provide painting and maintenance services to local families, community spaces, and people in need — completely free of charge.
        </p>
        <p className="text-gray-600 text-base leading-relaxed">
          We are proud to make a positive impact across Wellington and the Hutt Valley by improving homes and shared spaces. Every year, we commit a portion of our time and resources to projects that make a real difference.
        </p>
      </div>
    </section>
  );
}

const communityProjects = [
  {
    id: 1,
    title: 'Solidarity Renovation',
    location: 'Newtown, Wellington',
    story: 'As part of our commitment to giving back, we recently donated a complete floor renovation to a homeowner in need. The new, modern flooring has completely transformed their space, bringing a sense of brightness and comfort—offered with no cost to them.',
    impact: 'Over 500 community members benefit from this space weekly.',
    before: 'https://static.readdy.ai/image/38235d25ca124ed59b2cbc0dcd639844/7ba054c643f7c62fb25dffdaac86d5c4.jpeg',
    after: 'https://static.readdy.ai/image/38235d25ca124ed59b2cbc0dcd639844/12b148eccb4b2e6f0e1cd99b50dcd984.jpeg',
  },
  {
    id: 2,
    title: 'Single Mother Family Home',
    location: 'Lower Hutt',
    story: 'A single mother of three was struggling to maintain her home after a difficult year. Through our Community Homes Program, we provided a full interior repaint at no cost, giving her family a fresh, clean home to be proud of.',
    impact: 'A family of four now lives in a safe, beautiful home.',
    before: 'https://readdy.ai/api/search-image?query=old%20worn%20family%20home%20interior%20before%20painting%20tired%20walls%20Lower%20Hutt%20New%20Zealand%20before%20renovation%20family%20home%20in%20need&width=700&height=480&seq=com2002b&orientation=landscape',
    after: 'https://readdy.ai/api/search-image?query=beautifully%20freshly%20painted%20family%20home%20interior%20Lower%20Hutt%20New%20Zealand%20after%20renovation%20bright%20clean%20walls%20transformation%20family%20home&width=700&height=480&seq=com2002a&orientation=landscape',
  },
];

const stats = [
  { value: '2+', label: 'Community Projects' },
  { value: '+', label: 'Families Helped' },
  { value: '+', label: 'Community Spaces' },
  { value: '100%', label: 'Volunteer Work' },
];

export default function CommunityPage() {
  const navigate = useNavigate();
  const [videoPlaying, setVideoPlaying] = useState(false);

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section
        className="relative pt-32 pb-20"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=community%20painting%20project%20volunteers%20helping%20local%20family%20Wellington%20New%20Zealand%20painters%20giving%20back%20community%20service%20team%20painting%20house%20together%20warm%20sunny%20day%20neighbourhood%20improvement&width=1440&height=500&seq=comhero001&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#1C1C1C]/80" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 text-center">
          <span className="inline-block text-gray-400 text-sm font-semibold tracking-widest uppercase mb-4">Giving Back</span>
          <h1 className="text-5xl md:text-6xl font-black text-white mb-5 tracking-tight">Supporting Our Community</h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            We don't just paint homes — we help improve lives across Wellington and the Hutt Valley.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-14 bg-[#2D2D2D]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {stats.map((stat) => (
              <div key={stat.label}>
                <div className="text-4xl font-black text-white mb-1">{stat.value}</div>
                <div className="text-white/70 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Intro */}
      <CommunityIntro />

      {/* Projects */}
      <main className="bg-[#F7F9FB] py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight">Community Projects</h2>
            <p className="text-gray-500 mt-3">Real stories, real impact.</p>
          </div>

          <div className="space-y-16">
            {communityProjects.map((project, i) => (
              <div
                key={project.id}
                className={`flex flex-col lg:flex-row gap-10 items-center ${i % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}
              >
                {/* Before & After */}
                <div className="w-full lg:w-[55%]">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="relative rounded-xl overflow-hidden h-56">
                      <img src={project.before} alt="Before" className="w-full h-full object-cover object-top" />
                      <div className="absolute top-2 left-2 bg-gray-800 text-white text-xs font-bold px-2.5 py-1 rounded-full">Before</div>
                    </div>
                    <div className="relative rounded-xl overflow-hidden h-56">
                      <img src={project.after} alt="After" className="w-full h-full object-cover object-top" />
                      <div className="absolute top-2 left-2 bg-green-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">After</div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="w-full lg:w-[45%]">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="ri-map-pin-line text-gray-400 text-sm" />
                    <span className="text-gray-400 text-sm font-semibold">{project.location}</span>
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-4 tracking-tight">{project.title}</h3>
                  <p className="text-gray-600 text-base leading-relaxed mb-5">{project.story}</p>

                </div>
              </div>
            ))}
          </div>

          {/* Video at end of projects */}
          <div className="mt-20 text-center">
            <span className="inline-block text-gray-400 text-sm font-semibold tracking-widest uppercase mb-3">Watch Our Work</span>
            <h3 className="text-2xl md:text-3xl font-black text-gray-900 mb-10 tracking-tight">See the Impact We Make</h3>
            <div className="flex justify-center">
              <div
                className="relative rounded-2xl overflow-hidden bg-black w-full max-w-[360px]"
                style={{ aspectRatio: '9/16' }}
              >
                {!videoPlaying ? (
                  <button
                    type="button"
                    onClick={() => setVideoPlaying(true)}
                    className="relative w-full h-full cursor-pointer group border-0 p-0 block"
                    style={{ aspectRatio: '9/16' }}
                  >
                    <img
                      src={COMMUNITY_VIDEO_THUMBNAIL}
                      alt="Community Homes Program video"
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-black/45 group-hover:bg-black/55 transition-colors" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-20 h-20 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm border-2 border-white/50 group-hover:bg-white/30 group-hover:scale-110 transition-all duration-300">
                        <i className="ri-play-fill text-white text-4xl ml-1" />
                      </div>
                    </div>
                    <div className="absolute bottom-5 left-1/2 -translate-x-1/2">
                      <span className="text-white/70 text-xs font-medium tracking-widest uppercase">Click to play</span>
                    </div>
                  </button>
                ) : (
                  <iframe
                    src={`https://www.youtube.com/embed/${COMMUNITY_VIDEO_ID}?autoplay=1&rel=0`}
                    title="Community Homes Program video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* CTA */}
      <section className="py-16 bg-[#1C1C1C] text-center">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <h2 className="text-3xl font-black text-white mb-4 tracking-tight">Know someone who needs help?</h2>
          <p className="text-white/60 text-lg mb-8">If you know a family or community space that could benefit from our program, get in touch.</p>
          <a
            href="/contact"
            onClick={(e) => { e.preventDefault(); navigate('/contact'); }}
            className="inline-flex items-center gap-2 px-10 py-4 bg-white text-[#1C1C1C] font-bold rounded-full whitespace-nowrap hover:bg-gray-100 transition-colors cursor-pointer text-base"
          >
            Contact Us <i className="ri-arrow-right-line" />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}